import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Plus, Trash2, Home, Sparkles, Calendar } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const ROOMS = ['Kök', 'Badrum', 'Sovrum', 'Vardagsrum', 'Tvättstuga', 'Hela hemmet', 'Annat'];
const FREQUENCIES = [
  { value: 'daily', label: 'Dagligen' },
  { value: 'weekly', label: 'Varje vecka' },
  { value: 'monthly', label: 'Varje månad' }
];

export default function CleaningPage() {
  const navigate = useNavigate();
  const { getAuthHeader, hasAccess } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newTask, setNewTask] = useState({ name: '', room: 'Kök', frequency: 'weekly' });
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/api/cleaning`, { headers: getAuthHeader() });
      setTasks(res.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleTask = async (taskId) => {
    try {
      await axios.put(`${API}/api/cleaning/${taskId}/toggle`, {}, { headers: getAuthHeader() });
      setTasks(tasks.map(t => t.id === taskId ? { ...t, completed: !t.completed } : t));
    } catch (error) {
      toast.error('Kunde inte uppdatera');
    }
  };

  const addTask = async () => {
    if (!newTask.name.trim()) return;
    try {
      const res = await axios.post(`${API}/api/cleaning`, newTask, { headers: getAuthHeader() });
      setTasks([...tasks, res.data]);
      setNewTask({ name: '', room: 'Kök', frequency: 'weekly' });
      setShowAdd(false);
      toast.success('Uppgift tillagd');
    } catch (error) {
      toast.error('Kunde inte lägga till');
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/api/cleaning/${taskId}`, { headers: getAuthHeader() });
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Uppgift borttagen');
    } catch (error) {
      toast.error('Kunde inte ta bort');
    }
  };

  const filteredTasks = tasks.filter(t => {
    if (filter === 'all') return true;
    return t.frequency === filter;
  });

  const completedCount = tasks.filter(t => t.completed).length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="cleaning-page">
      <header className="border-b border-border/30 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" onClick={() => navigate('/category/home')} className="rounded-full w-9 h-9 p-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-heading text-lg font-semibold">Städschema</h1>
            <p className="text-xs text-muted-foreground">{completedCount} av {tasks.length} klart</p>
          </div>
          <Button onClick={() => setShowAdd(true)} className="rounded-full" size="sm">
            <Plus className="w-4 h-4 mr-1" /> Ny
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Progress */}
        <div className="bg-white rounded-2xl p-4 border border-border/30">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Veckoframsteg</span>
            <span className="text-sm text-muted-foreground">{Math.round((completedCount / tasks.length) * 100 || 0)}%</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${(completedCount / tasks.length) * 100 || 0}%` }}
            />
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {[{ value: 'all', label: 'Alla' }, ...FREQUENCIES].map(f => (
            <Button
              key={f.value}
              variant={filter === f.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(f.value)}
              className="rounded-full whitespace-nowrap"
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Tasks */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4].map(i => <div key={i} className="h-16 bg-secondary/50 rounded-xl animate-pulse" />)}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredTasks.map(task => (
              <div 
                key={task.id}
                className={`bg-white rounded-xl p-4 border border-border/30 flex items-center gap-3 transition-all ${task.completed ? 'opacity-60' : ''}`}
              >
                <Checkbox 
                  checked={task.completed} 
                  onCheckedChange={() => toggleTask(task.id)}
                  className="h-5 w-5"
                />
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${task.completed ? 'line-through text-muted-foreground' : ''}`}>
                    {task.name}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-center gap-2">
                    <Home className="w-3 h-3" /> {task.room}
                    <span className="text-border">•</span>
                    <Calendar className="w-3 h-3" /> {FREQUENCIES.find(f => f.value === task.frequency)?.label}
                  </p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => deleteTask(task.id)} className="text-muted-foreground hover:text-destructive">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Add Task Modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4 animate-slide-up">
              <h3 className="font-heading text-lg font-semibold">Lägg till uppgift</h3>
              
              <Input
                placeholder="Uppgiftens namn"
                value={newTask.name}
                onChange={(e) => setNewTask({...newTask, name: e.target.value})}
                className="rounded-xl"
              />
              
              <div>
                <label className="text-sm font-medium mb-2 block">Rum</label>
                <div className="flex flex-wrap gap-2">
                  {ROOMS.map(room => (
                    <Button
                      key={room}
                      variant={newTask.room === room ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewTask({...newTask, room})}
                      className="rounded-full"
                    >
                      {room}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Frekvens</label>
                <div className="flex gap-2">
                  {FREQUENCIES.map(f => (
                    <Button
                      key={f.value}
                      variant={newTask.frequency === f.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setNewTask({...newTask, frequency: f.value})}
                      className="rounded-full flex-1"
                    >
                      {f.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowAdd(false)} className="flex-1 rounded-full">
                  Avbryt
                </Button>
                <Button onClick={addTask} className="flex-1 rounded-full">
                  Lägg till
                </Button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
